import React, { useState } from "react";
import {
  Box,
  Flex,
  Stack,
  Text,
  Icon,
  useColorModeValue,
  HStack,
  Button,
  Collapse,
  useMediaQuery,
  Select,
} from "@chakra-ui/react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { IoMdSettings, IoMdLogOut, IoMdPerson, IoMdCard } from "react-icons/io";
import { PeerxLogo } from "./icons/Icons";
import { useAuth } from "../context/AuthProvider";
import { useTranslation } from "react-i18next";


const Sidebar = ({ routes = [], isOpen, onClose }) => {
  const { t, i18n } = useTranslation();

  const location = useLocation();
  const [isLargerThanMd] = useMediaQuery("(min-width: 768px)");
  const textColor = useColorModeValue("whiteAlpha.700", "whiteAlpha.900");
  const activeColor = useColorModeValue("whiteAlpha.900", "blue.300");
  const bgActive = useColorModeValue("gray.100", "blue.900");
  const { logOut } = useAuth();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logOut();
      navigate("/auth/sign-in");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  /**
   * Helper to check if a route is active
   * @param {string} routePath - The route's path
   * @returns {boolean} Whether the route is active
   */
  const isActiveRoute = (routePath) => location.pathname === routePath;

  /**
   * Helper to check if a submenu (like settings) is active
   * @param {string} basePath - The base path for the submenu (e.g., "/personal/profile")
   * @returns {boolean} Whether the submenu is active
   */
  const isSubmenuActive = (basePath) => location.pathname.startsWith(basePath);

  /**
   * Helper to check if the settings section is active
   */
  const isSettingsSectionActive = () =>
    isSubmenuActive("/personal/profile") || isSubmenuActive("/personal/billing");

    // Handle language change
    const handleLanguageChange = (event) => {
      i18n.changeLanguage(event.target.value);
    };

  return (
    <Box
      bg="brand.900"
      color={textColor}
      position={isLargerThanMd ? "fixed" : "relative"}
      left="0"
      top="0"
      h={isLargerThanMd ? "100vh" : "auto"}
      w={isLargerThanMd ? "250px" : "100%"}
      shadow={isLargerThanMd ? "md" : "none"}
      zIndex="100"
      transition="transform 0.3s ease-in-out"
      transform={isLargerThanMd ? "translateX(0)" : isOpen ? "translateX(0)" : "translateX(-100%)"}
      borderRight={isLargerThanMd ? "1px solid gray.200" : "none"}
    >
      <Flex align="center" justifyContent="space-between" px={6} py={4}>
        <HStack spacing={4} align="center">
          <Flex as={RouterLink} to="/" alignItems="center">
            <PeerxLogo h="90px" w="160px" />
          </Flex>
        </HStack>
        {!isLargerThanMd && <Button onClick={onClose} colorScheme="gray" mr={4}>            
          {t("sidebar.close")}
        </Button>}
      </Flex>


      <Stack bg="brand.800" borderRadius="2xl" spacing={6} mt={4} px={6} py={14}>
        {routes.map((route, idx) => {
          const isActive = isActiveRoute(route.path);

          return (
            <Flex
              key={idx}
              as={RouterLink}
              to={route.path}
              align="center"
              p={2}
              borderRadius="md"
              bg={isActive ? bgActive : ""}
              _hover={{ bg: "gray.100" }}
              transition="background-color 0.2s ease-in-out"
            >
              <Icon as={route.icon} boxSize={5} color={isActive ? activeColor : textColor} />
              <Text ml={4} fontSize="lg" fontWeight={isActive ? "bold" : "normal"} color={isActive ? activeColor : textColor}>
                {route.name}
              </Text>
            </Flex>
          );
        })}
      </Stack>

      <Stack bg="brand.700" height="600px" borderRadius="2xl" spacing={6} px={6} pb={6} py={4} mt="-20px">
        {/* Settings Section */}
        <Flex
          align="center"
          p={3}
          borderRadius="md"
          cursor="pointer"
          _hover={{ bg: "gray.100" }}
          onClick={() => setSettingsOpen(!settingsOpen)}
        >
          <Icon as={IoMdSettings} boxSize={5} color={textColor} />
          <Text ml={4} fontSize="lg">{t("sidebar.settings")}</Text>
        </Flex>
        <Collapse in={settingsOpen || isSettingsSectionActive()} animateOpacity>
          <Stack pl={8} spacing={2} py={2}>
            <Flex
              as={RouterLink}
              to="/personal/profile"
              align="center"
              p={2}
              borderRadius="md"
              _hover={{ bg: "gray.100" }}
              bg={isSubmenuActive("/personal/profile") ? bgActive : ""}
            >
              <Icon as={IoMdPerson} boxSize={4} color={isSubmenuActive("/personal/profile") ? activeColor : textColor} />
              <Text ml={4} fontSize="lg">{t("sidebar.profile")}</Text>
            </Flex>
            {/* <Flex
              as={RouterLink}
              to="/personal/billing"
              align="center"
              p={2}
              borderRadius="md"
              _hover={{ bg: "gray.100" }}
              bg={isSubmenuActive("/personal/billing") ? bgActive : ""}
            >
              <Icon as={IoMdCard} boxSize={4} color={isSubmenuActive("/personal/billing") ? activeColor : textColor} />
              <Text ml={4} fontSize="lg">{t("sidebar.billing")}</Text>
            </Flex> */}
          </Stack>
        </Collapse>

        {/* Logout Button */}
        <Button
          onClick={handleLogout}
          alignItems="center"
          p={3}
          borderRadius="xl"
          color="whiteAlpha.900"
          _hover={{ bg: "red.700" }}
          leftIcon={<IoMdLogOut />}
          justifyContent="flex-start"
          variant='solid'
          colorScheme='red'
          bg="red"
          >
          <Text ml={2} fontSize="md">{t("sidebar.log_out")}</Text>
          </Button>
      

      {/* Language Selector */}
      <Box px={6} py={4}>
        <Select value={i18n.language} onChange={handleLanguageChange} bg="white" color="black">
          <option value="en">English</option>
          <option value="fr">Français</option>
          <option value="es">Español</option>
        </Select>
      </Box>
      </Stack>
    </Box>
  );
};

export default Sidebar;
